import "./index.css";
import { StrictMode, useEffect, type ComponentType } from "react";
import { createRoot } from "react-dom/client";
import Layout from "./components/Layout";
import HomeLayout from "./components/Layout/HomeLayout";
import { UserProvider } from "./lib/contexts/UserContext";
import { PluginListProvider } from "./lib/contexts/PluginListContext";
import { McpStatusProvider } from "./lib/contexts/McpStatusContext";
import { LiveAnnouncer } from "./components/Layout/LiveAnnouncer";
import {
  DashboardFallback,
  PageFallback,
} from "./components/Layout/RouteFallback";
import PageNotFound from "./PageNotFound";
import HomePage from "./components/Home/HomePage";
import { HelmetProvider } from "react-helmet-async";
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigate,
  useNavigation,
} from "react-router";
import ErrorPage from "./ErrorPage"; // Import your new component
import { logError } from "./lib/api/Api";
import { fromBrowserExtension } from "./lib/errorOrigin";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { initI18n } from "./i18n";
import {
  hasBotAdded,
  trackBotAdded,
  trackPageView,
  withoutBotAdded,
} from "./lib/analytics";

// A report that cannot be delivered (offline, API down) is dropped here. Left
// unhandled, its rejection comes back through onunhandledrejection as a new
// error to report, whose report fails the same way: one error became a report
// every second or so for as long as the endpoint stayed unreachable. Catching
// it in these two handlers bounds that for every caller — ErrorPage's and
// ErrorBoundary's unhandled failures land here once, and stop.
const report = (error: Parameters<typeof logError>[0]) => {
  logError(error).catch(() => {});
};

// Global error handlers for uncaught errors. Extension scripts injected into
// the page land here too; their failures are not ours to report.
window.onerror = (message, source, lineno, colno, error) => {
  if (fromBrowserExtension(error?.stack ?? source)) return;
  report({
    message: typeof message === "string" ? message : "Unknown error",
    stack: error?.stack,
    info: {
      source,
      lineno,
      colno,
      type: "window.onerror",
    },
    originalError: error ?? undefined,
  });
};

window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  const error = event.reason;
  if (error instanceof Error && fromBrowserExtension(error.stack)) return;
  report({
    message:
      error instanceof Error
        ? error.message
        : typeof error === "string"
        ? error
        : "Unhandled Promise Rejection",
    stack: error instanceof Error ? error.stack : undefined,
    info: {
      type: "unhandledrejection",
      reason: error instanceof Error ? { name: error.name } : error,
    },
    originalError: error instanceof Error ? error : undefined,
  });
};

// Module scope, so StrictMode's repeated effects cannot count an invite twice.
let landingUrlChecked = false;
// Set from the landing URL; sent right after the next page_view that goes out.
let botAddedPending = false;

/**
 * Google Analytics page views: one for the landing URL, then one per
 * client-side navigation (index.html turns gtag's automatic page_view off).
 * lib/analytics strips Discord IDs from every URL it sends.
 *
 * Also turns the ?bot_added=1 the API appends to a completed bot invite's
 * redirect into a single bot_added event, then drops the marker from the
 * address bar so a reload or a copied link cannot count it again.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the entry module is never a Fast Refresh boundary; editing it reloads the page
function AnalyticsTracker() {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  // Dropping the marker is not a new page.
  const page = pathname + withoutBotAdded(location.search);
  // A first visit to a code-split page (lazyPage, below) renders the route's
  // fallback until its code arrives, and the page's own <SEO> title only
  // after that: a view sent any earlier would carry index.html's title, the
  // homepage's. The router flips `initialized` in the same update that
  // renders the route, and useNavigation re-renders this component on every
  // router update, so the view goes out one frame after that render instead.
  useNavigation();
  const routeRendered = router.state.initialized;

  useEffect(() => {
    if (!routeRendered) return;
    // react-helmet-async writes the new <title> in an animation frame queued
    // during this commit; one frame later page_title is the new page's.
    // Hidden tabs run no frames, so a background tab sends when first shown.
    const frame = requestAnimationFrame(() => {
      // A redirect issued during this commit (plugin setup's jump to role
      // links) has already replaced the URL; its own render sends the view.
      if (window.location.pathname !== pathname) return;
      trackPageView();
      if (botAddedPending) {
        botAddedPending = false;
        trackBotAdded();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [page, pathname, routeRendered]);

  useEffect(() => {
    // Only the URL the app was loaded with can carry the marker.
    if (landingUrlChecked) return;
    landingUrlChecked = true;
    const search = withoutBotAdded(location.search);
    if (search === location.search) return;
    // Sent by the page_view effect, right after the page it happened on.
    if (hasBotAdded(location.search)) botAddedPending = true;
    navigate(
      { pathname: location.pathname, search, hash: location.hash },
      { replace: true, state: location.state, preventScrollReset: true }
    );
  }, [location, navigate]);

  return null;
}

/**
 * Route-level code splitting. A first visit to the homepage — the page most
 * organic search visits land on — used to download, parse and run the whole
 * app before anything painted: the dashboard, the rule editor, Stripe, the
 * legal text. The entry bundle now holds the shell (providers, layouts, the
 * homepage, the 404 and error pages) and every other page is fetched the
 * first time it is needed.
 *
 * Through React Router's route `lazy`, not React.lazy + Suspense:
 *  - The router fetches the code of every lazy route a URL matches at once,
 *    before rendering any of them. /dashboard/:guildId/:ruleId needs
 *    SidebarLayout and RuleEditor; under React.lazy the editor's chunk would
 *    only be requested once SidebarLayout rendered its <Outlet> — after the
 *    server's data had loaded — and a second, different skeleton would take
 *    over the one already on screen: a waterfall, then a layout shift.
 *  - After the first render, a navigation fetches the next route's code
 *    before it commits, so the current page stays up until the new one can
 *    render. (React.lazy would do the same here: RouterProvider runs every
 *    update in a transition, which keeps a Suspense fallback from showing.
 *    The first point is the one that decides it. WARM_NEXT, below, fetches
 *    the likeliest next chunks early so that wait is rarely felt.)
 *  - The catch is the first render: while the lazy routes a URL matches are
 *    loading, the data router renders a route's hydrateFallbackElement in
 *    place of the first one — and with none, it renders nothing at all. So
 *    every route that can be the first lazy match of a URL carries one, and
 *    the layouts above it render as usual: a direct load of /dashboard/<id>
 *    shows the real frame at once, and the fallback in the route's slot is
 *    shaped like what replaces it (components/Layout/RouteFallback.tsx).
 *    SidebarLayout's children never need one: they can only load alongside
 *    SidebarLayout, whose fallback stands in for all of them.
 */
type Page = ComponentType;

/** The component of a page whose module exports it as its default. */
const defaultExport = (mod: { default: Page }) => mod.default;

/**
 * How long a route load the stale-deploy handler has taken over (below) waits
 * for the reload that should replace the page. Past this the visitor is still
 * here — they answered "Stay" to the rule editor's unsaved-changes prompt —
 * and the load fails for real instead.
 */
const PARKED_LOAD_MS = 15_000;

/** A route `lazy` that imports a page's module and renders `pick(module)`. */
const lazyPage =
  <M,>(load: () => Promise<M>, pick: (mod: M) => Page) =>
  async () => {
    const mod = await load();
    // Only undefined when the vite:preloadError handler below has taken the
    // failure and is reloading the page: staying pending keeps the fallback,
    // or the page being left, on screen until the reload replaces it. It
    // must still settle. React Router keeps a route's lazy() promise for the
    // life of the tab, so one that never settled would leave every later
    // link to this route doing nothing at all. Failing after the window
    // lands those clicks on ErrorPage and its Reload button instead.
    if (mod === undefined) {
      return new Promise<never>((_, reject) => {
        window.setTimeout(
          () => reject(new Error("This page's code could not be loaded.")),
          PARKED_LOAD_MS
        );
      });
    }
    return { Component: pick(mod) };
  };

// 1. Define routes as an object
const router = createBrowserRouter([
  {
    element: (
      <>
        <AnalyticsTracker />
        <Layout />
      </>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <HomeLayout />,
        children: [
          { index: true, element: <HomePage /> },
          {
            path: "upgrade",
            lazy: lazyPage(
              () => import("./components/Quota/QuotaManager"),
              defaultExport
            ),
            hydrateFallbackElement: <PageFallback />,
          },
          {
            path: "dashboard",
            lazy: lazyPage(
              () => import("./components/Guild/GuildList"),
              defaultExport
            ),
            hydrateFallbackElement: <PageFallback />,
          },
          // One chunk for both: they share Legal.tsx. It stays one module
          // because scripts/generate-homepage.ts reads the <SEO> props there.
          {
            path: "privacy",
            lazy: lazyPage(
              () => import("./components/Legal"),
              (legal) => legal.PrivacyPolicy
            ),
            hydrateFallbackElement: <PageFallback />,
          },
          {
            path: "terms",
            lazy: lazyPage(
              () => import("./components/Legal"),
              (legal) => legal.TermsOfService
            ),
            hydrateFallbackElement: <PageFallback />,
          },
          {
            path: "status",
            lazy: lazyPage(
              () => import("./components/Status/StatusPage"),
              defaultExport
            ),
            hydrateFallbackElement: <PageFallback />,
          },
          // AI connections are account-level, not per-server: one grant can
          // cover several servers, and it is revoked in one place.
          {
            path: "connections",
            lazy: lazyPage(
              () => import("./components/Connections/AiConnections"),
              defaultExport
            ),
            hydrateFallbackElement: <PageFallback />,
          },
          {
            path: "connections/authorize",
            lazy: lazyPage(
              () => import("./components/Connections/AuthorizeConsent"),
              defaultExport
            ),
            hydrateFallbackElement: <PageFallback />,
          },
        ],
      },
      {
        path: "dashboard/:guildId",
        lazy: lazyPage(
          () => import("./components/Layout/SidebarLayout"),
          defaultExport
        ),
        hydrateFallbackElement: <DashboardFallback />,
        children: [
          {
            index: true,
            lazy: lazyPage(
              () => import("./components/Dashboard/DashboardOverview"),
              defaultExport
            ),
          },
          {
            path: ":ruleId",
            lazy: lazyPage(
              () => import("./components/Dashboard/RuleEditor"),
              defaultExport
            ),
          },
        ],
      },
      // Catch-all 404 route
      {
        path: "*",
        element: <PageNotFound />,
      },
    ],
  },
]);

/**
 * Stale deploys. Chunk file names are content hashes and a deploy replaces
 * the whole site, so a tab opened before a deploy asks for chunks that no
 * longer exist the first time it needs a page it has not loaded yet — the
 * host answers with the SPA's index.html, and the import fails. Vite reports
 * every failed dynamic import with this event
 * (https://vite.dev/guide/build#load-error-handling).
 *
 * A failed route load — a first visit's, or a navigation's — is answered
 * with a full page load of where the visitor was going: fresh HTML, current
 * chunk names. preventDefault keeps the failure away from the router, so the
 * route's fallback or the page being left stays up until that load replaces
 * it (lazyPage parks the undefined module it gets instead). Other dynamic
 * imports — a language switch, the error reporter's stack mapper — have
 * fallbacks of their own and are not worth pulling the page out from under
 * someone for.
 *
 * At most one reload a minute, remembered in sessionStorage: if the fresh
 * page fails the same way, staleness was not the problem, and the error goes
 * through to the router's errorElement — ErrorPage and its Reload button —
 * instead of looping. With no sessionStorage there is no telling, so no
 * automatic reload at all.
 */
const STALE_RELOAD_AT_KEY = "rl:stale-chunk-reload-at";
const STALE_RELOAD_INTERVAL_MS = 60_000;
let staleReloadStarted = false;
// Set once this page has asked the browser for a fresh load; never cleared.
let replacedByReload = false;

window.addEventListener("vite:preloadError", (event) => {
  const { initialized, navigation } = router.state;
  // Not a route load: that import's own fallback handles it.
  if (initialized && navigation.state === "idle") return;
  // Offline, a fresh load would swap the working app for the browser's
  // offline page. Let the failure through to ErrorPage instead: its Reload
  // button works once the connection is back.
  if (navigator.onLine === false) return;
  if (!staleReloadStarted) {
    try {
      const last = Number(sessionStorage.getItem(STALE_RELOAD_AT_KEY));
      if (Date.now() - last < STALE_RELOAD_INTERVAL_MS) return;
      sessionStorage.setItem(STALE_RELOAD_AT_KEY, String(Date.now()));
    } catch {
      return;
    }
    staleReloadStarted = true;
    replacedByReload = true;
    // Still here this much later, the visitor declined to leave (the rule
    // editor's unsaved-changes prompt): stop parking failures for a reload
    // that is not coming, so the next one reaches the error page. The parked
    // load itself fails on the same clock (PARKED_LOAD_MS, in lazyPage),
    // after keeping the editor and its unsaved work on screen that long.
    setTimeout(() => (staleReloadStarted = false), PARKED_LOAD_MS);
    // A navigation has not changed the URL yet: load its destination, not
    // the page it is leaving.
    if (navigation.location) {
      window.location.assign(router.createHref(navigation.location));
    } else {
      window.location.reload();
    }
  }
  event.preventDefault();
});

// The page this handler left can come back: Back restores it from the
// back/forward cache, parked route load and all, and the router would keep
// that load for good. A fresh load is what the visitor was sent to anyway.
window.addEventListener("pageshow", (event) => {
  if (event.persisted && replacedByReload) window.location.reload();
});

/**
 * Warms the code a visitor is likely to open next while they read the page
 * they are on, so the first click into the dashboard or the rule editor is
 * not a second of nothing: a data router keeps the current page up, with no
 * other sign of life, until the next route's code has arrived — and the
 * editor is the largest chunk (~140 kB gzip). Idle time only, once per chunk;
 * the lazy routes above import the very same modules, so they find them
 * loaded. A warm-up that fails (a stale deploy) is dropped here: the handler
 * above ignores imports made while the router is idle, and the real
 * navigation later gets the usual one reload.
 */
const WARM_NEXT: ReadonlyArray<[RegExp, () => Promise<unknown>]> = [
  // The server list: the server dashboard is the next click.
  [/^\/dashboard\/?$/, () => import("./components/Layout/SidebarLayout")],
  [/^\/dashboard\/?$/, () => import("./components/Dashboard/DashboardOverview")],
  // A server's overview: a rule, "Create rule", Test or Role links next.
  [/^\/dashboard\/[^/]+\/?$/, () => import("./components/Dashboard/RuleEditor")],
];
const warmed = new Set<number>();
const whenIdle = (task: () => void) =>
  typeof window.requestIdleCallback === "function"
    ? window.requestIdleCallback(task, { timeout: 5_000 })
    : window.setTimeout(task, 1_500);

router.subscribe((state) => {
  if (!state.initialized || state.navigation.state !== "idle") return;
  WARM_NEXT.forEach(([path, load], index) => {
    if (warmed.has(index) || !path.test(state.location.pathname)) return;
    warmed.add(index);
    whenIdle(() => void load().catch(() => {}));
  });
});

/**
 * The homepage ships a hidden crawler fallback after #root (injected by
 * scripts/generate-homepage.ts) so bots that never run JavaScript — GPTBot,
 * ClaudeBot, plain scrapers — still get the page's content. Anything that DID
 * run this bundle has the real React tree instead, and leaving a hidden
 * duplicate of the same headings in that DOM is indistinguishable from hidden
 * keyword text. Dropping it once React has mounted means only the clients
 * that need it ever see it.
 *
 * Removal happens after render, not before: if mounting fails, the fallback
 * is the only content on the page and must survive.
 */
const dropCrawlerFallback = () => {
  document.getElementById("rl-seo-fallback")?.remove();
};

// 2. Initialize i18n before mounting so the static homepage stays visible
//    until React can render the final tree in one paint. Mount anyway if
//    i18n init fails so the app is never permanently blank.
const mount = () => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <ErrorBoundary>
        <HelmetProvider>
          <UserProvider>
            <LiveAnnouncer />
            {/* Inside UserProvider: the counts are per-account and the poll
                does not start until someone is logged in. */}
            <McpStatusProvider>
              <PluginListProvider>
                <RouterProvider router={router} />
              </PluginListProvider>
            </McpStatusProvider>
          </UserProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </StrictMode>
  );
  dropCrawlerFallback();
};

initI18n().then(mount, (err) => {
  console.error("[i18n] init failed, mounting anyway:", err);
  mount();
});
